import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { appointmentAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Phone,
  MapPin,
  Check,
  X,
  BadgeCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const AdminAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login');
    } else {
      fetchAppointments();
    }
  }, [navigate]);

  const fetchAppointments = async () => {
    try {
      const res = await appointmentAPI.getAll();
      setAppointments(res.data);
    } catch {
      toast.error('Randevular yüklenemedi');
    }
  };

  // ================= ACTIONS =================

  const updateStatus = (id, status) => {
    setAppointments(prev =>
      prev.map(a => (a.id === id ? { ...a, status } : a))
    );
  };

  const handleApprove = async (id) => {
    if (!confirm('Randevu onaylansın mı?')) return;

    try {
      await appointmentAPI.approve(id);
      updateStatus(id, 'approved');
      toast.success('Randevu onaylandı');
    } catch (error) {
      toast.error('Onaylanamadı');
    }
  };

  const handleComplete = async (id) => {
    if (!confirm('Servis tamamlandı mı?')) return;

    try {
      await appointmentAPI.complete(id);
      updateStatus(id, 'completed');
      toast.success('Randevu tamamlandı');
    } catch {
      toast.error('Tamamlanamadı');
    }
  };

  const handleCancel = async (id) => {
    if (!confirm('İptal etmek istediğinize emin misiniz?')) return;

    try {
      await appointmentAPI.cancel(id);
      updateStatus(id, 'cancelled');
      toast.success('Randevu iptal edildi');
    } catch {
      toast.error('İptal edilemedi');
    }
  };

  // ================= STATUS BADGE =================

  const getStatusBadge = (status) => {
    const map = {
      pending: {
        label: 'Bekliyor',
        class: 'bg-yellow-100 text-yellow-800',
      },
      approved: {
        label: 'Onaylandı',
        class: 'bg-blue-100 text-blue-800',
      },
      completed: {
        label: 'Tamamlandı',
        class: 'bg-green-100 text-green-800',
      },
      cancelled: {
        label: 'İptal',
        class: 'bg-red-100 text-red-800',
      },
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${map[status].class}`}
      >
        {map[status].label}
      </span>
    );
  };

  // ================= UI =================

  return (
    <div className="min-h-screen bg-light">
      {/* NAV */}
      <nav className="bg-navy text-white p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-2 hover:text-orange"
          >
            <ArrowLeft size={18} />
            Dashboard'a Dön
          </Link>

          <h1 className="text-xl font-bold">
            Randevu Yönetimi
          </h1>
        </div>
      </nav>

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto px-6 py-10 space-y-4">
        {appointments.map((apt) => (
          <div
            key={apt.id}
            className="bg-white rounded-xl shadow-sm p-6 border-l-4
            hover:shadow-md transition"
          >
            {/* HEADER */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-navy text-lg">
                  {apt.full_name}
                </h3>
                <p className="text-sm text-slate-500">
                  {apt.service_type}
                </p>
              </div>

              {getStatusBadge(apt.status)}
            </div>

            {/* INFO GRID */}
            <div className="grid md:grid-cols-2 gap-3 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <Calendar size={15} />
                {apt.appointment_date}
              </div>

              <div className="flex items-center gap-2">
                <Clock size={15} />
                {apt.appointment_time}
              </div>

              <div className="flex items-center gap-2">
                <Phone size={15} />
                {apt.phone}
              </div>

              <div className="flex items-center gap-2">
                <MapPin size={15} />
                {apt.address}
              </div>
            </div>

            {/* NOTES */}
            {apt.notes && (
              <p className="mt-3 text-sm text-slate-600">
                <strong>Not:</strong> {apt.notes}
              </p>
            )}

            {/* ACTIONS */}
            <div className="flex gap-2 mt-5">
              {apt.status === 'pending' && (
                <>
                  <Button
                    onClick={() => handleApprove(apt.id)}
                    className="bg-blue-600 hover:bg-blue-700"
                    size="sm"
                  >
                    <BadgeCheck size={14} className="mr-1" />
                    Onayla
                  </Button>

                  <Button
                    onClick={() => handleCancel(apt.id)}
                    variant="destructive"
                    size="sm"
                  >
                    <X size={14} className="mr-1" />
                    İptal
                  </Button>
                </>
              )}

              {apt.status === 'approved' && (
                <>
                  <Button
                    onClick={() => handleComplete(apt.id)}
                    className="bg-green-600 hover:bg-green-700"
                    size="sm"
                  >
                    <Check size={14} className="mr-1" />
                    Tamamlandı
                  </Button>

                  <Button
                    onClick={() => handleCancel(apt.id)}
                    variant="destructive"
                    size="sm"
                  >
                    <X size={14} className="mr-1" />
                    İptal
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}

        {appointments.length === 0 && (
          <div className="text-center text-slate-500 py-20">
            Henüz randevu bulunmuyor.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAppointments;
